import { useEffect } from "react";
import {
  Navigate,
  createFileRoute,
  useRouterState,
} from "@tanstack/react-router";

import {
  calendarMonthParam,
  currentCalendarMonth,
  enumParam,
} from "../../../lib/url-state";
import { AdminPage } from "../../../workspaces/label/pages/AdminPage";
import {
  ADMIN_SECTIONS,
  type AdminSection,
} from "../../../workspaces/label/page-search";
import { canAdminister } from "../../../workspaces/label/nav";
import { useSeat } from "../../../workspaces/label/seat";

/* Seats, payroll, quality issues, and the manager-gated corrections dictionary.
 *
 * The standalone app expressed the role gate by not MOUNTING this route, so an
 * annotator who typed `/admin` fell through to the catch-all and landed back on
 * the workbench. A file route is always mounted, so the gate is stated here
 * instead — and it produces the same absence: the same redirect, no error page,
 * no hint that a screen exists. The server gate is unchanged and independent;
 * every admin call is manager/customer-checked at the door. */
export const Route = createFileRoute("/_console/label/admin")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { readonly month?: string; readonly section?: AdminSection } => ({
    section: enumParam(search.section, ADMIN_SECTIONS),
    month: calendarMonthParam(search.month, { max: currentCalendarMonth() }),
  }),
  component: Admin,
});

function Admin() {
  const seat = useSeat();
  const { month, section } = Route.useSearch();
  const navigate = Route.useNavigate();
  const searchStr = useRouterState({
    select: (router) => router.location.searchStr,
  });
  useEffect(() => {
    if (month === undefined && new URLSearchParams(searchStr).has("month")) {
      void navigate({
        replace: true,
        search: (current) => ({ ...current, month: undefined }),
      });
    }
  }, [month, navigate, searchStr]);
  if (!canAdminister(seat.role)) return <Navigate to="/label" replace />;
  return (
    <AdminPage
      user={seat}
      month={month}
      section={section}
      onMonthChange={(patch, options) =>
        void navigate({
          replace: options?.replace,
          search: (current) => ({ ...current, ...patch }),
        })
      }
    />
  );
}
